import {
  hasAllPermissions,
  hasAnyPermission,
  permissionCodeSchema,
  type PermissionCode,
  type PermissionSession
} from '@super-mega/server/contracts/authorization'

/** UI 操作标识显式映射业务权限，服务端仍在领域入口检查。 */
export function actionPermissions(
  resource: string,
  action: string
): { all?: PermissionCode[]; any?: PermissionCode[] } {
  if (resource === 'subject' && action === 'restore') return { all: ['subject.archive'] }
  if (resource === 'contract.template' && action === 'restore') return { all: ['contract.template.restore'] }
  if (
    resource === 'room' &&
    (action.startsWith('status-') || ['vacant', 'unavailable', 'available', 'disabled', 'maintenance'].includes(action))
  )
    return { all: ['room.status.update'] }
  if (resource === 'bill' && action === 'group') return { all: ['bill.group.create'] }
  if (resource === 'bill' && action === 'money') return { any: ['bill.receipt.record', 'bill.payment.record'] }
  if (resource === 'contract.document' && action === 'status')
    return { any: ['contract.document.confirm', 'contract.document.activate', 'contract.document.void'] }
  if (resource === 'account' && action === 'permissions') return { all: ['account.roles.assign'] }
  if (resource === 'customer' && (action === 'add-tags' || action === 'clear-tags'))
    return { all: ['customer.tag.update'] }
  const fixed: Partial<Record<string, PermissionCode[]>> = {
    bills: ['bill.read'],
    contracts: ['contract.document.read'],
    documents: ['contract.document.read'],
    rooms: ['route.address.rooms', 'room.read'],
    'batch-create-rooms': ['room.create'],
    refund: ['bill.refund.record'],
    receipt: ['bill.receipt.record'],
    payment: ['bill.payment.record'],
    materials: [resource === 'subject' ? 'subject.material.update' : 'order.rental.material.update'],
    contract: ['contract.document.read'],
    supplement: ['subject.material.update'],
    rentals: ['route.orders.rentals', 'order.rental.read'],
    services: ['route.orders.service', 'order.service.read'],
    'create-rental': ['route.orders.rentals', 'order.rental.create'],
    'create-service': ['route.orders.service', 'order.service.create'],
    'issue-bills': ['bill.issue', 'bill.group.create', 'bill.group.issue'],
    contractStatus: [],
    forceRemove: [],
    forceDelete: [],
    amount: []
  }
  if (action === 'contractStatus')
    return { any: ['contract.document.confirm', 'contract.document.activate', 'contract.document.void'] }
  if (action === 'amount') return { any: ['bill.update', 'bill.adjust'] }
  if (action === 'forceRemove' || action === 'forceDelete') {
    const parsed = permissionCodeSchema.safeParse(`${resource}.forceDelete`)
    return { all: parsed.success ? [parsed.data] : ['member.delete'] }
  }
  if (fixed[action]) return { all: fixed[action] }
  const actions: Record<string, string> = {
    view: 'read',
    details: 'read',
    preview: 'read',
    print: 'read',
    history: 'read',
    versions: 'read',
    edit: 'update',
    content: 'update',
    update: 'update',
    note: 'update',
    collection: 'update',
    create: 'create',
    new: 'create',
    copy: 'create',
    delete: 'delete',
    remove: 'delete',
    archive: 'archive',
    restore: 'status.update',
    status: 'status.update',
    toggle: 'status.update',
    enable: 'status.update',
    disable: 'status.update',
    include: 'update',
    publish: 'publish',
    activate: 'activate',
    effective: 'activate',
    confirm: 'confirm',
    confirmed: 'confirm',
    void: 'void',
    voided: 'void',
    issue: 'issue',
    salesperson: 'update',
    customer: 'update',
    group: 'update',
    'add-tags': 'update',
    'clear-tags': 'update',
    'monthly-billing': 'billing.update',
    ownership: 'update',
    'quick-create': 'create',
    renew: 'renew',
    cancel: 'cancel',
    complete: 'complete',
    end: 'end',
    terminate: 'terminate',
    reprice: 'reprice'
  }
  const suffix = actions[action]
  if (!suffix) return { all: ['role.read'] }
  const code = permissionCodeSchema.safeParse(`${resource}.${suffix}`)
  return { all: code.success ? [code.data] : ['role.read'] }
}

export function canPerformAction(session: PermissionSession | null, resource: string, action: string) {
  if (!session) return false
  const rule = actionPermissions(resource, action)
  return (!rule.all || hasAllPermissions(session, rule.all)) && (!rule.any || hasAnyPermission(session, rule.any))
}
